import { ApiError, toApiErrorBody } from './api-error';
import { getAccessToken, getCsrfToken, renovarAccessToken } from './auth-token';

const API_BASE = '/api';

/** Métodos que não mutam estado não precisam do token de CSRF. */
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Obrigatória nas operações críticas de escrita. */
  idempotencyKey?: string;
  /** Rotas de autenticação que ainda não têm sessão (login, verify) dispensam o token. */
  skipAuth?: boolean;
  /**
   * Como ler o corpo da resposta **bem-sucedida**. `json` por padrão.
   *
   * `text` e `blob` existem para o que não é JSON — CSV de exportação, modelo de planilha. O corpo de
   * **erro** continua sendo lido como JSON em qualquer caso: o envelope de erro vale para toda
   * falha, inclusive a de uma rota que responderia texto no sucesso.
   */
  parse?: 'json' | 'text' | 'blob';
}

/**
 * Cliente HTTP único da aplicação.
 *
 * `credentials: 'include'` porque o refresh token vive em cookie httpOnly (ADR-003); a
 * API fica na mesma origem, atrás do proxy do Vite em desenvolvimento e servindo o próprio
 * front em homologação e produção (ADR-014).
 */
/**
 * Envio de arquivo com progresso.
 *
 * **XHR, e não `fetch`.** O `fetch` não expõe progresso de upload — só de download —, e um
 * envio de 15 MB sem barra é uma tela parada que a pessoa interpreta como travamento.
 * `ReadableStream` no corpo resolveria, mas exige HTTP/2 e não é suportado de forma
 * confiável em todos os navegadores.
 *
 * Fica **aqui**, e não solto no módulo que faz o upload, porque `Authorization`, CSRF e a
 * distinção entre falha de rede e erro do servidor são as mesmas — e duplicá-las é como
 * uma das duas portas um dia esquece o CSRF.
 */
export async function apiUpload<TResponse>(
  path: string,
  body: FormData,
  onProgress?: (percent: number) => void,
): Promise<TResponse> {
  /*
   * Mesma renovação do `apiRequest`, e aqui ela dói mais: o 401 chega **depois** de o arquivo
   * inteiro subir. Sem repetir, quem escolheu um arquivo grande depois de quinze minutos de
   * tela aberta perdia o envio e não sabia por quê.
   *
   * `FormData` pode ser enviado de novo — o XHR não o consome —, então a repetição é o mesmo
   * envio, com o token novo.
   */
  try {
    return await enviarArquivo<TResponse>(path, body, onProgress);
  } catch (erro) {
    if (!(erro instanceof ApiError) || erro.status !== 401) throw erro;

    const novoToken = await renovarAccessToken();

    if (!novoToken) throw erro;

    return enviarArquivo<TResponse>(path, body, onProgress);
  }
}

function enviarArquivo<TResponse>(
  path: string,
  body: FormData,
  onProgress?: (percent: number) => void,
): Promise<TResponse> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();

    request.open('POST', `${API_BASE}${path}`);
    request.withCredentials = true;

    const accessToken = getAccessToken();
    const csrf = getCsrfToken();

    if (accessToken) request.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    if (csrf) request.setRequestHeader('X-CSRF-Token', csrf);
    // Sem `Content-Type`: o próprio XHR monta o `multipart/form-data` com o boundary.
    // Defini-lo à mão quebra o parse no servidor, e o erro é confuso.

    if (onProgress) {
      request.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
      });
    }

    request.addEventListener('load', () => {
      const payload: unknown = request.responseText ? JSON.parse(request.responseText) : null;

      if (request.status >= 200 && request.status < 300) {
        resolve(payload as TResponse);
        return;
      }

      reject(new ApiError(request.status, toApiErrorBody(payload, request.status)));
    });

    // Status 0 distingue rede indisponível de erro devolvido pelo servidor.
    request.addEventListener('error', () => reject(new ApiError(0, toApiErrorBody(null, 0))));
    request.addEventListener('abort', () => reject(new ApiError(0, toApiErrorBody(null, 0))));

    request.send(body);
  });
}

export async function apiRequest<TResponse>(
  path: string,
  opcoes: RequestOptions = {},
): Promise<TResponse> {
  /*
   * ## 401 renova e repete, uma vez
   *
   * O access token vive quinze minutos e a renovação só acontecia no **carregamento da
   * página**. Com a aba aberta, passados quinze minutos, toda requisição passava a responder
   * 401 e ninguém renovava — a sessão "caía" sozinha, sem aviso nenhum.
   *
   * Aumentar a validade do token seria tratar o sintoma e piorar o resto: token longo é token
   * que continua valendo depois de a pessoa sair. O cookie de renovação já dura sete dias; o
   * que faltava era usá-lo quando o token expira, e não só quando a página recarrega.
   *
   * **Uma** repetição, e só quando a renovação devolve token novo. Sem esse limite, um 401 que
   * não é de expiração — permissão revogada, por exemplo — viraria laço infinito entre
   * requisição e renovação.
   */
  try {
    return await enviar<TResponse>(path, opcoes);
  } catch (erro) {
    const expirou = erro instanceof ApiError && erro.status === 401 && !opcoes.skipAuth;

    if (!expirou) throw erro;

    const novoToken = await renovarAccessToken();

    if (!novoToken) throw erro;

    return enviar<TResponse>(path, opcoes);
  }
}

async function enviar<TResponse>(
  path: string,
  { body, idempotencyKey, skipAuth, parse = 'json', headers, ...init }: RequestOptions = {},
): Promise<TResponse> {
  const requestHeaders = new Headers(headers);
  const method = (init.method ?? 'GET').toUpperCase();

  if (body !== undefined) {
    requestHeaders.set('Content-Type', 'application/json');
  }

  if (idempotencyKey) {
    requestHeaders.set('Idempotency-Key', idempotencyKey);
  }

  const accessToken = getAccessToken();

  if (!skipAuth && accessToken) {
    requestHeaders.set('Authorization', `Bearer ${accessToken}`);
  }

  // Double submit: o backend confere o header contra o cookie. Aplicado no cliente
  // único para nenhuma escrita nova precisar lembrar disso.
  if (!SAFE_METHODS.has(method)) {
    const csrf = getCsrfToken();

    if (csrf) requestHeaders.set('X-CSRF-Token', csrf);
  }

  let response: Response;

  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: requestHeaders,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // Rede indisponível: status 0 distingue isso de erro devolvido pelo servidor.
    throw new ApiError(0, toApiErrorBody(null, 0));
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  if (!response.ok) {
    const erro: unknown = await response.json().catch(() => null);

    throw new ApiError(response.status, toApiErrorBody(erro, response.status));
  }

  if (parse === 'text') return (await response.text()) as TResponse;
  if (parse === 'blob') return (await response.blob()) as TResponse;

  return (await response.json().catch(() => null)) as TResponse;
}
